/**
 * Ads API - Unified ad operations with proper auth
 */

import { apiFetch, toUserError, ErrorCodes } from "./httpClient";
import { uploadImages } from "./upload";

// Hub types
export const HubType = {
  MAIN: 'main',
  LADIES: 'ladies',
  FARMER: 'farmer',
  GENERAL: 'general'
};

// Visibility types
export const Visibility = {
  PUBLIC: 'public',
  LADIES: 'ladies',
  FARMER: 'farmer'
};

/**
 * Unified publish function for both Main FAB and Ladies Hub FAB
 * Uses apiFetch which ALWAYS includes auth token
 */
export async function publishAdUnified({ draft, images = [], hub = "main" }) {
  console.log(`[Ads] Publishing ad for hub: ${hub}, images: ${images.length}`);
  
  // Step 1: Upload all images first
  let imageUrls = [];
  if (images && images.length > 0) {
    imageUrls = await uploadImages(images);
  }
  
  // Step 2: Build payload
  const payload = {
    // Core fields
    title: draft.title,
    category: draft.category,
    subcategory: draft.subcategory || null,
    subcategory_id: draft.subcategory_id || null,
    condition: draft.condition || null,
    price: parseFloat(draft.price) || 0,
    discount_percent: draft.discountPercent ? parseFloat(draft.discountPercent) : null,
    location: draft.location,
    description: draft.description,
    currency: draft.currency || 'SDG',
    images: imageUrls,

    // Location — legacy flat fields (kept for backwards compatibility).
    latitude: draft.latitude || null,
    longitude: draft.longitude || null,
    city: draft.city || null,
    state: draft.state || null,

    // iter322 Phase B — Structured location. **REQUIRED** by the
    // backend POST /api/ads guard. Without these, every publish
    // dies with ERR_LOCATION_COUNTRY_REQUIRED — which is exactly
    // what produced the P0 "Country is required" bug reported by
    // the operator after step 4 / Publish was tapped.
    // iter337 — Forward `city` (Locality/County/District) too so all
    // 4 required levels reach the backend. `area` already does.
    country: draft.country || null,
    country_name: draft.country_name || null,
    region: draft.region || null,
    area: draft.area || null,
    location_label: draft.location_label || null,
    location_confirmed: Boolean(draft.location_confirmed),

    // iter334 — Optional free-text city + GPS coordinates from the
    // "Use my current location" picker. Backend stores them for
    // display only; strict filtering uses `city` when present.
    location_manual_city: draft.location_manual_city || null,
    location_lat: draft.location_lat ?? draft.latitude ?? null,
    location_lng: draft.location_lng ?? draft.longitude ?? null,
    // iter337 — Level-5 optional "Extra location details" free-text.
    // Display only; NOT a primary search/nearby filter.
    location_extra: draft.location_extra || null,

    // iter328 — Seller-provided delivery option.
    delivery_available: Boolean(draft.delivery_available),
    delivery_type: draft.delivery_type || 'none',
    delivery_fee_amount: draft.delivery_fee_amount ?? null,
    delivery_fee_currency: draft.delivery_fee_currency || null,
    delivery_area: draft.delivery_area || null,
    delivery_notes: draft.delivery_notes || null,

    // Hub context
    hub: hub,
    visibility: hub === 'ladies' ? 'ladies' : 'public',

    // Category fields
    category_main: draft.category_main || null,
    category_sub: draft.category_sub || null,
    category_path: draft.category_path || draft.categoryPath || null,
    category_other: draft.category_other || null,

    // Contact info — legacy full-string + iter334 split.
    seller_phone: draft.seller_phone || null,
    seller_whatsapp: draft.use_same_for_whatsapp
      ? draft.seller_phone || null
      : draft.seller_whatsapp || null,
    seller_messenger: draft.seller_messenger || null,
    phone_country_code: draft.phone_country_code || null,
    phone_local: draft.phone_local || null,

    // Quality flags
    low_quality_image_count: draft.low_quality_image_count || 0,

    // Publishing
    is_public: true,
    status: 'active'
  };
  
  // Step 3: Create ad
  const r = await apiFetch('/api/ads', { method: 'POST', body: payload, timeoutMs: 60000 });
  
  if (!r.ok) {
    const msg = toUserError(r);
    const error = new Error(msg);
    error.status = r.status;
    error.stage = 'create';
    error.data = r.data;
    
    // Map error codes
    if (r.status === 401) {
      error.code = ErrorCodes.AUTH_EXPIRED;
    } else if (r.status === 403) {
      error.code = ErrorCodes.FORBIDDEN;
    } else if (r.status === 429) {
      error.code = ErrorCodes.RATE_LIMITED;
    } else if (r.status === 400 || r.status === 422) {
      error.code = ErrorCodes.VALIDATION_ERROR;
    } else {
      error.code = ErrorCodes.PUBLISH_FAILED;
    }
    
    throw error;
  }
  
  console.log(`[Ads] Published successfully: ${r.data?.ad_id}`);
  return r.data;
}

/**
 * Get user's ads
 */
export async function getUserAds(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  
  const endpoint = `/api/ads/user/me${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return apiFetch(endpoint);
}

/**
 * Get ad by ID
 */
export async function getAd(adId) {
  return apiFetch(`/api/ads/${adId}`);
}

/**
 * Update ad
 */
export async function updateAd(adId, updates) {
  return apiFetch(`/api/ads/${adId}`, { method: 'PATCH', body: updates });
}

/**
 * Delete ad
 */
export async function deleteAd(adId) {
  return apiFetch(`/api/ads/${adId}`, { method: 'DELETE' });
}

/**
 * Mark ad as sold
 */
export async function markAdSold(adId) {
  return apiFetch(`/api/ads/${adId}/mark-sold`, { method: 'POST' });
}

/**
 * Mark ad as available
 */
export async function markAdAvailable(adId) {
  return apiFetch(`/api/ads/${adId}/mark-available`, { method: 'POST' });
}

export default {
  HubType,
  Visibility,
  publishAdUnified,
  getUserAds,
  getAd,
  updateAd,
  deleteAd,
  markAdSold,
  markAdAvailable
};
